import {
  CreatePopulation,
  EngagementId,
  Paginated,
  PaginationArgs,
  Population,
  PopulationField,
  PopulationId,
  PopulationMin,
  PopulationRow,
  SourceId,
} from "platformed-client/types";
import {invalidateQueries, q} from "../../state";
import {platformedClient} from "..";

export async function create(engagementId: EngagementId, body: CreatePopulation): Promise<Population> {
  const population = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/populations",
    {path: {engagement_id: engagementId}, body},
  );
  await invalidateQueries([q.tprm.engagementPopulations.filter(engagementId)]);
  return population;
}

export async function updateName(populationId: PopulationId, name: string): Promise<void> {
  await platformedClient.internalRequest("post", "/region/{region}/api/tprm/populations/{population_id}/name", {
    path: {population_id: populationId},
    body: {name},
  });
  // A population's name is denormalised into every sample drawn from it as
  // `SampleMin.population_name`, so the samples lists carry a stale copy until
  // they are invalidated too.
  await invalidateQueries([
    q.tprm.engagementPopulations.filter(),
    q.tprm.population.filter(populationId),
    q.tprm.engagementSamples.filter(),
    q.tprm.populationSamples.filter(),
  ]);
}

/** `null` clears the description; the column holds no empty string. */
export async function updateDescription(populationId: PopulationId, description: string | null): Promise<void> {
  await platformedClient.internalRequest("post", "/region/{region}/api/tprm/populations/{population_id}/description", {
    path: {population_id: populationId},
    body: {description},
  });
  // The populations table renders the description as subtext under the name, so
  // the list is as stale as the detail page after an edit.
  await invalidateQueries([q.tprm.engagementPopulations.filter(), q.tprm.population.filter(populationId)]);
}

/**
 * The population's commit verb, and its re-extraction: pins the sources, stamps
 * the commit, and runs extraction. Rejected (409) when the population has no
 * sources.
 */
export async function extract(populationId: PopulationId): Promise<Population> {
  const population = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/populations/{population_id}/extract",
    {path: {population_id: populationId}},
  );
  // Rows are replaced when extraction completes; invalidate both so the page flips
  // to Processing and refreshes its data.
  await invalidateQueries([
    q.tprm.population.filter(populationId),
    q.tprm.populationRows.filter(populationId),
    q.tprm.engagementPopulations.filter(),
  ]);
  return population;
}

/**
 * Mint a second population over the same sources, named `<name> (copy)`, and
 * extract it — the way to take a changed source while the samples already drawn
 * keep the rows they hold. Available while the population is locked.
 */
export async function clone(populationId: PopulationId): Promise<Population> {
  const population = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/populations/{population_id}/clone",
    {path: {population_id: populationId}},
  );
  await invalidateQueries([q.tprm.engagementPopulations.filter()]);
  return population;
}

export async function updateAgentPrompt(populationId: PopulationId, agentPrompt: string | null): Promise<void> {
  await platformedClient.internalRequest("post", "/region/{region}/api/tprm/populations/{population_id}/agent-prompt", {
    path: {population_id: populationId},
    body: agentPrompt,
  });
  await invalidateQueries([q.tprm.population.filter(populationId)]);
}

export async function addSources(populationId: PopulationId, sourceIds: SourceId[]): Promise<void> {
  await platformedClient.internalRequest("post", "/region/{region}/api/tprm/populations/{population_id}/sources", {
    path: {population_id: populationId},
    body: sourceIds,
  });
  await invalidateQueries([q.tprm.population.filter(populationId), q.tprm.engagementPopulations.filter()]);
}

export async function removeSource(populationId: PopulationId, sourceId: SourceId): Promise<void> {
  await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/populations/{population_id}/sources/{source_id}",
    {path: {population_id: populationId, source_id: sourceId}},
  );
  await invalidateQueries([q.tprm.population.filter(populationId), q.tprm.engagementPopulations.filter()]);
}

export async function setFields(populationId: PopulationId, fields: PopulationField[]): Promise<void> {
  await platformedClient.internalRequest("put", "/region/{region}/api/tprm/populations/{population_id}/fields", {
    path: {population_id: populationId},
    body: fields,
  });
  await invalidateQueries(q.tprm.populationRecordsReplaced(populationId));
}

/**
 * Locking freezes a population's name, description, sources, fields and rows.
 * The first draw from it takes the lock automatically, and taking it again
 * coalesces, so locking a locked population changes nothing.
 */
export async function lock(populationId: PopulationId): Promise<Population> {
  const population = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/populations/{population_id}/lock",
    {path: {population_id: populationId}},
  );
  await invalidateLock(populationId);
  return population;
}

/** The lock's only release. The samples drawn from the population are left as they are. */
export async function unlock(populationId: PopulationId): Promise<Population> {
  const population = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/populations/{population_id}/unlock",
    {path: {population_id: populationId}},
  );
  await invalidateLock(populationId);
  return population;
}

// A row's rename and delete are gated on the lock, so the lists go as stale as
// the population itself.
async function invalidateLock(populationId: PopulationId): Promise<void> {
  await invalidateQueries([q.tprm.population.filter(populationId), q.tprm.engagementPopulations.filter()]);
}

export async function get(populationId: PopulationId): Promise<Population> {
  return await platformedClient.internalRequest("get", "/region/{region}/api/tprm/populations/{population_id}", {
    path: {population_id: populationId},
  });
}

export async function listRows(populationId: PopulationId, query: PaginationArgs): Promise<Paginated<PopulationRow>> {
  return await platformedClient.internalRequest("get", "/region/{region}/api/tprm/populations/{population_id}/rows", {
    path: {population_id: populationId},
    query,
  });
}

export async function list(engagementId: EngagementId): Promise<PopulationMin[]> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/populations",
    {path: {engagement_id: engagementId}},
  );
}

export async function delete_(populationId: PopulationId): Promise<void> {
  await platformedClient.internalRequest("delete", "/region/{region}/api/tprm/populations/{population_id}", {
    path: {population_id: populationId},
  });
  // Invalidate only the list query — never the deleted population's own `population`/`populationRows`
  // queries: invalidation re-fetches every matching cached query (observed or not), which would 404 on
  // the now-deleted id. The stale detail entries are simply left to be garbage-collected.
  await invalidateQueries([q.tprm.engagementPopulations.filter()]);
}
